import {
  isCodeReferencesEnabled,
  isDisableCodeReferencesWithOverrideEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, Map, OrderedMap, Record } from '@gonfalon/immutable-shim/immutableShim';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { Repo } from '#utils/codeRefs/types';
import type { Member } from '#utils/members/records/Member';

import { type Branch, createBranch } from './Branch';
import { type RepositoryFormRecord, createRepositoryFormRecord } from './RepositoryFormRecord';

export class Repository extends Record<Repo>({
  id: '',
  _access: Map(),
  _links: Map(),
  defaultBranch: '',
  enabled: false,
  name: '',
  sourceLink: '',
  type: '',
  branches: OrderedMap(),
  commitUrlTemplate: '',
  hunkUrlTemplate: '',
  version: 0,
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this.get('_access');

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  getId() {
    return this.name;
  }

  hasReferences() {
    return this.branches.size > 0;
  }

  // Returns the default branch if it exists
  getDefaultDisplayBranch(): Branch | undefined {
    if (!this.branches.size) {
      return undefined;
    }
    const defaultBranch = this.branches.find((b) => b.name === this.defaultBranch);
    // We will get rid of returning first behavior after
    // when launch filter
    if (!isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled()) {
      return defaultBranch;
    }
    return defaultBranch || this.branches.first();
  }

  generateCollapsedState(collapsed: boolean) {
    return this.branches.reduce((acc, branch) => acc.set(branch.name, branch.generateCollapsedState(collapsed)), Map());
  }

  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  getFilteredBranch(branchFilter: string): Branch | undefined {
    if (branchFilter) {
      return this.branches?.find((b: Branch) => b.name === branchFilter);
    }
    return this.getDefaultDisplayBranch();
  }

  toForm() {
    return createRepositoryFormRecord({
      enabled: this.enabled,
    });
  }

  mergeForm(form: RepositoryFormRecord) {
    return this.withMutations((repository) =>
      repository.merge({
        enabled: form.enabled,
      }),
    );
  }
}

export function createRepository(props = {}) {
  const repo = props instanceof Repository ? props : new Repository(fromJS(props));
  return repo
    .update(
      'branches',
      (bs) =>
        bs &&
        bs.reduce((acc, curr) => {
          const currBranch = createBranch(curr);
          return acc.set(
            currBranch.name,
            currBranch.applyCommitUrlTemplate(repo.commitUrlTemplate).applyHunkUrlTemplate(repo.hunkUrlTemplate),
          );
        }, OrderedMap()),
    )
    .set('id', repo.getId());
}
